import { GraphQLError } from 'graphql';

import { ErrorOrWarning } from '../service/ErrorHelpers/ErrorHelpers';

import {
    GdAdditionalTargetingJoinType,
    GdAdditionalTargetingJoinTypeInput,
    GdAdditionalTargetingMode,
    GdAdditionalTargetingModeInput,
    GdAdditionalTargetingUatraitsInput,
    GdAdditionalTargetingVersionedInput
} from './graphql-grid';
import { GroupId } from './groups';
import { EditInternalGroupDataValue } from './groupsEditState';

export interface TargetingYandexUids {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingInternalNetwork {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    uncheckAll?: boolean;
}

export interface TargetingIsTablet {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    uncheckAll?: boolean;
}

export interface TargetingIsMobile {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    uncheckAll?: boolean;
}

export interface TargetingIsTouch {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    uncheckAll?: boolean;
}

export interface TargetingIsBrowser {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    uncheckAll?: boolean;
}

export interface TargetingOsNames {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: GdAdditionalTargetingUatraitsInput[];
}

export interface TargetingOsFamilies {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: GdAdditionalTargetingVersionedInput[];
}

export interface TargetingBrowserNames {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: GdAdditionalTargetingVersionedInput[];
}

export interface TargetingBrowserEngines {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: GdAdditionalTargetingVersionedInput[];
}

export interface TargetingDeviceNames {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingDeviceVendors {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: GdAdditionalTargetingUatraitsInput[];
}

export interface TargetingQueryReferers {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingInterfaceLangs {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingUserAgents {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingShowDates {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: GraphQlLocalDate[];
}

export interface TargetingClidTypes {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingClids {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingDesktopInstalledApps {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: GraphQlLong[];
}

export interface TargetingMobileInstalledApps {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingQueryOptions {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingTestIds {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: GraphQlLong[];
}

export interface TargetingYsCookies {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    value: string[];
}

export interface TargetingIsYandexPlus {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    uncheckAll?: boolean;
}

export interface TargetingIsVirused {
    targetingMode: GdAdditionalTargetingMode;
    joinType: GdAdditionalTargetingJoinType;
    uncheckAll?: boolean;
}

export interface ApiFormatTargetingShowDates {
    targetingMode: GdAdditionalTargetingModeInput;
    joinType: GdAdditionalTargetingJoinTypeInput;
    value: GraphQlLocalDate[];
}

export enum AdditionalTargetingField {
    YANDEX_UIDS = 'targetingYandexUids',
    OS_NAMES = 'targetingOsNames',
    OS_FAMILIES = 'targetingOsFamilies',
    BROWSER_NAMES = 'targetingBrowserNames',
    BROWSER_ENGINES = 'targetingBrowserEngines',
    DEVICE_NAMES = 'targetingDeviceNames',
    DEVICE_VENDORS = 'targetingDeviceVendors',
    USER_AGENTS = 'targetingUserAgents',
    QUERY_REFERERS = 'targetingQueryReferers',
    INTERFACE_LANGS = 'targetingInterfaceLangs',
    INTERNAL_NETWORK = 'targetingInternalNetwork',
    IS_BROWSER = 'targetingIsBrowser',
    IS_MOBILE = 'targetingIsMobile',
    IS_TABLET = 'targetingIsTablet',
    IS_TOUCH = 'targetingIsTouch',
    SHOW_DATES = 'targetingShowDates',
    CLID_TYPES = 'targetingClidTypes',
    CLIDS = 'targetingClids',
    DESKTOP_INSTALLED_APPS = 'targetingDesktopInstalledApps',
    MOBILE_INSTALLED_APPS = 'targetingMobileInstalledApps',
    QUERY_OPTIONS = 'targetingQueryOptions',
    TEST_IDS = 'targetingTestIds',
    YS_COOKIES = 'targetingYsCookies',
    IS_YANDEX_PLUS = 'targetingIsYandexPlus',
    DEVICE_OPTIONS = 'deviceOptions',
    IS_VIRUSED = 'targetingIsVirused'
}

export enum RestInternalGroupField {
    LEVEL = 'level',
    REGIONS = 'regions',
    NAME = 'name',
    START_TIME = 'startTime',
    FINISH_TIME = 'finishTime',
    CAMPAIGN_TYPE = 'campaignType',
    GROUP_TYPE = 'groupType'
}

export type InternalGroupField = RestInternalGroupField | AdditionalTargetingField;

export type InputExternalValueKey =
    | AdditionalTargetingField.YANDEX_UIDS
    | AdditionalTargetingField.DEVICE_NAMES
    | AdditionalTargetingField.USER_AGENTS
    | AdditionalTargetingField.QUERY_REFERERS
    | AdditionalTargetingField.SHOW_DATES
    | AdditionalTargetingField.CLID_TYPES
    | AdditionalTargetingField.CLIDS
    | AdditionalTargetingField.DESKTOP_INSTALLED_APPS
    | AdditionalTargetingField.MOBILE_INSTALLED_APPS
    | AdditionalTargetingField.QUERY_OPTIONS
    | AdditionalTargetingField.TEST_IDS
    | AdditionalTargetingField.YS_COOKIES;

export type TextTargetingKey =
    | AdditionalTargetingField.DEVICE_NAMES
    | AdditionalTargetingField.USER_AGENTS
    | AdditionalTargetingField.QUERY_REFERERS
    | AdditionalTargetingField.QUERY_OPTIONS
    | AdditionalTargetingField.YS_COOKIES;

export type NumericTargetingKey =
    | AdditionalTargetingField.CLID_TYPES
    | AdditionalTargetingField.CLIDS
    | AdditionalTargetingField.TEST_IDS;

export type SuggestTargetingKey =
    | AdditionalTargetingField.DESKTOP_INSTALLED_APPS
    | AdditionalTargetingField.INTERFACE_LANGS;

export type SuggestWithStringDictTargetingKey =
    | AdditionalTargetingField.DEVICE_VENDORS
    | AdditionalTargetingField.OS_NAMES;

export type SuggestWithVersionTargetingKey =
    | AdditionalTargetingField.OS_FAMILIES
    | AdditionalTargetingField.BROWSER_ENGINES
    | AdditionalTargetingField.BROWSER_NAMES;

export type InputExternalValue = Required<EditInternalGroupDataValue>[InputExternalValueKey];

export type SuggestTargetingValue = Required<EditInternalGroupDataValue>[SuggestTargetingKey];

export type SuggestWithStringDictTargetingValue = Required<
    EditInternalGroupDataValue
>[SuggestWithStringDictTargetingKey];

export type SuggestWithVersionTargetingValue = Required<EditInternalGroupDataValue>[SuggestWithVersionTargetingKey];

export type DeviceOptionsKey =
    | AdditionalTargetingField.IS_MOBILE
    | AdditionalTargetingField.IS_TABLET
    | AdditionalTargetingField.IS_TOUCH;

export type RadioTargetingKey =
    | AdditionalTargetingField.INTERNAL_NETWORK
    | AdditionalTargetingField.IS_YANDEX_PLUS
    | AdditionalTargetingField.IS_VIRUSED;

export type RadioTargetingValue = Required<EditInternalGroupDataValue>[RadioTargetingKey];

export type DeviceOptionsValue = EditInternalGroupDataValue[DeviceOptionsKey];

export type DeviceOptionsRecord = Partial<Record<DeviceOptionsKey, DeviceOptionsValue>>;

export enum TagCloudTargetingToggleEditorValue {
    YES = 'yes',
    NO = 'no',
    NOT_SELECTED = 'not-selected'
}

export enum ApplicationStore {
    GOOGLE_PLAY = 'Google Play',
    APP_STORE = 'App Store'
}

export enum OsType {
    IOS = 'iOS',
    ANDROID = 'Android'
}

export interface InternalGroupsSaveResponse {
    updatedGroupIds: GroupId[];
    validations?: ErrorOrWarning[];
    readonly errors?: GraphQLError[];
}

export enum TargetingGroupType {
    CORE = 'core',
    DISTRIBUTION = 'distribution',
    TECHNICAL = 'technical',
    DEVICE = 'device',
    OS = 'os',
    BROWSER = 'browser',
    OTHER = 'other'
}
